import React, { useEffect, useState } from 'react'
import { Modal, Button, Form, Row, Col } from 'react-bootstrap'
import Select from 'react-select'
import { useQuery } from '@apollo/client'
import { MultiSelectWithActions } from 'app/tfi/components/MultiSelectWithActions'
import { DateInput } from 'app/tfi/components/DateInput'
import { GET_ALL_POLICIES_STATUSES, GET_ALL_POLICIES_TYPES } from '../../../gql/queries/policiesQueries'
import { GET_ALL_USERS_FILTER, GET_ASSIGNMENT_USERS_LIST } from '../../../gql/queries/usersQuery'
import { GET_ALL_LEAD_SOURCES } from '../../../gql/queries/leadSourceQuery'
import { parseId } from '../../../helpers'
import {
  IMMIGRATION_STATUS_CHOICES,
  DOCUMENT_STATUS_OPTIONS,
  PAYMENT_STATUS_OPTIONS,
  GENDER_OPTIONS,
  METAL_LEVELS,
} from '../../../helpers/catalogs'
import { RecipientFilters } from '../recipientsTypes'

const YES_NO_OPTIONS = [
  { value: 'true', label: 'Yes' },
  { value: 'false', label: 'No' },
]

const CARRIER_STATUS_OPTIONS = [
  { value: 'IN_FORCE', label: 'In Force' },
  { value: 'TERMINATED', label: 'Terminated' },
  { value: 'GRACE_PERIOD', label: 'Grace Period' },
  { value: 'PENDING_EFFECTUATION', label: 'Pending Effectuation' },
  { value: 'PAST_DUE', label: 'Past Due' },
  { value: 'DELINQUENT', label: 'Delinquent' },
  { value: 'FUTURE_EFFECTIVE', label: 'Future Effective' },
  { value: 'UNKNOWN', label: 'Unknown' },
]

const COMMISSION_PAYMENT_STATUS_OPTIONS = [
  { value: 'NEVER_STARTED', label: 'Never Started' },
  { value: 'AT_RISK', label: 'At Risk' },
  { value: 'CANCELED', label: 'Canceled' },
]

function boolVal(val: boolean | undefined): { value: string; label: string } | null {
  if (val === true) return { value: 'true', label: 'Yes' }
  if (val === false) return { value: 'false', label: 'No' }
  return null
}

function parseBool(v: string | undefined): boolean | undefined {
  if (v === 'true') return true
  if (v === 'false') return false
  return undefined
}

interface StateOption { id: string; name: string; code: string }
interface CarrierOption { id: string; name: string }

interface Props {
  show: boolean
  onHide: () => void
  filters: RecipientFilters
  onApply: (filters: Partial<RecipientFilters>) => void
  states?: StateOption[]
  carriers?: CarrierOption[]
}

export const RecipientAdvancedFiltersModal: React.FC<Props> = ({
  show,
  onHide,
  filters,
  onApply,
  states = [],
  carriers = [],
}) => {
  const [pending, setPending] = useState<RecipientFilters>(filters)

  useEffect(() => {
    if (show) setPending(filters)
  }, [show]) // eslint-disable-line react-hooks/exhaustive-deps

  const { data: policyStatusesData } = useQuery(GET_ALL_POLICIES_STATUSES, { skip: !show })
  const { data: policyTypesData } = useQuery(GET_ALL_POLICIES_TYPES, {
    skip: !show,
    variables: { first: 100 },
  })
  const { data: agentsData } = useQuery(GET_ALL_USERS_FILTER, {
    skip: !show,
    variables: { requireNpn: true },
  })
  const { data: assignedToData } = useQuery(GET_ASSIGNMENT_USERS_LIST, {
    skip: !show,
    variables: { assignmentCandidates: true },
  })
  const { data: leadSourcesData } = useQuery(GET_ALL_LEAD_SOURCES, {
    skip: !show,
    variables: { first: 200 },
  })

  // Policy statuses grouped
  const rawStatuses: { code: string; title: string; category: string }[] =
    policyStatusesData?.allPolicyStatuses?.edges?.map((e: any) => e.node) ?? []
  const grouped: Record<string, { value: string; label: string }[]> = {}
  rawStatuses.forEach((s) => {
    const cat = s.category || 'OTHER'
    if (!grouped[cat]) grouped[cat] = []
    grouped[cat].push({ value: s.code, label: s.title })
  })
  const policyStatusGrouped = Object.keys(grouped).map((cat) => ({
    label: cat.replace(/_/g, ' '),
    options: grouped[cat],
  }))
  const allStatusFlat = policyStatusGrouped.flatMap((g) => g.options)

  const policyTypeOptions: { value: string; label: string }[] =
    policyTypesData?.allPoliciesType?.edges?.map((e: any) => ({
      value: String(parseId(e.node.id)),
      label: e.node.typePolicy,
    })) ?? []

  const agentsOptions: { value: string; label: string }[] =
    agentsData?.allUsers?.edges?.map((e: any) => ({
      value: String(parseId(e.node.id)),
      label: `${e.node.firstName} ${e.node.lastName} - ${e.node.email}${e.node.npn ? ` (${e.node.npn})` : ''}`,
    })) ?? []

  const assignedToOptions: { value: string; label: string }[] =
    assignedToData?.allUsersList?.map((u: any) => ({
      value: u.id,
      label: `${u.firstName} ${u.lastName} - ${u.email}${u.npn ? ` (${u.npn})` : ''}`,
    })) ?? []

  const leadSourceOptions: { value: string; label: string }[] =
    leadSourcesData?.allLeadSources?.edges?.map((e: any) => ({
      value: String(parseId(e.node.id)),
      label: e.node.name,
    })) ?? []

  const stateOptions = states.map((s) => ({ value: s.code, label: `${s.name} (${s.code})` }))
  const carrierOptions = carriers.map((c) => ({ value: c.id, label: c.name }))

  const set = (patch: Partial<RecipientFilters>) => setPending((f) => ({ ...f, ...patch }))
  const multi = (field: keyof RecipientFilters, sel: any) =>
    set({ [field]: sel ? sel.map((o: any) => o.value) : [] } as any)

  const handleDateChange = (name: string, value: string) =>
    setPending((f) => ({ ...f, [name]: value }))

  const policyRelationshipValue =
    pending.withoutPolicy === true
      ? 'no_policy'
      : pending.isPolicyHolder === true
        ? 'holder'
        : pending.isPolicyHolder === false && pending.withoutPolicy === false
          ? 'family'
          : ''

  const handleApply = () => {
    onApply(pending)
    onHide()
  }

  const handleClear = () =>
    setPending({
      ...filters,
      state: [],
      carrier: [],
      policyStatus: [],
      policyType: [],
      isPolicyHolder: undefined,
      withoutPolicy: undefined,
      unassigned: undefined,
      user: [],
      assignedTo: [],
      stateJurisdictionType: '',
      zipCode: '',
      immigrationStatus: [],
      documentStatus: [],
      paymentStatus: [],
      leadSource: [],
      metalLevel: '',
      holderGender: '',
      hasMemberId: undefined,
      commissionPayable: undefined,
      commissionPaymentStatus: '',
      ownsCar: undefined,
      ownsHome: undefined,
      hasAutoPolicy: undefined,
      hasHomePolicy: undefined,
      policyCreatedFrom: '',
      policyCreatedTo: '',
      submissionDateFrom: '',
      submissionDateTo: '',
      carrierStatusGeneric: [],
      carrierLastAsOfDateAfter: '',
      carrierLastAsOfDateBefore: '',
      carrierIsAgeIn: undefined,
      carrierIsDelinquent: undefined,
      carrierIsUnpaidPending: undefined,
      carrierIsUnpaidPastDue: undefined,
    })

  return (
    <Modal show={show} onHide={onHide} size='xl' centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title>Advanced Filters</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className='d-flex justify-content-end gap-2 mb-4'>
          <Button variant='light' className='px-3' onClick={handleClear}>Clear</Button>
          <Button variant='primary' className='px-3' onClick={handleApply}>
            <i className='bi bi-search me-1' />Apply Filters
          </Button>
        </div>

        <Form>
          {/* ── Agent / Assignment ── */}
          <Row className='mb-3 g-3 row-cols-1 row-cols-md-3'>
            <Col>
              <Form.Label>Agent</Form.Label>
              <MultiSelectWithActions
                value={agentsOptions.filter((o) => pending.user.includes(o.value))}
                options={agentsOptions}
                onChange={(sel: any) => multi('user', sel)}
              />
            </Col>
            <Col>
              <Form.Label>Assigned To</Form.Label>
              <MultiSelectWithActions
                value={assignedToOptions.filter((o) => pending.assignedTo.includes(o.value))}
                options={assignedToOptions}
                onChange={(sel: any) => multi('assignedTo', sel)}
              />
            </Col>
            <Col>
              <Form.Label>Carrier</Form.Label>
              <MultiSelectWithActions
                value={carrierOptions.filter((o) => pending.carrier.includes(String(o.value)))}
                options={carrierOptions}
                onChange={(sel: any) => multi('carrier', sel)}
              />
            </Col>
            <Col>
              <Form.Label>State</Form.Label>
              <MultiSelectWithActions
                value={stateOptions.filter((o) => pending.state.includes(o.value))}
                options={stateOptions}
                onChange={(sel: any) => multi('state', sel)}
              />
            </Col>
            <Col>
              <Form.Label>State Jurisdiction Type</Form.Label>
              <Select
                value={
                  pending.stateJurisdictionType === 'FEDERAL'
                    ? { value: 'FEDERAL', label: 'Federal' }
                    : pending.stateJurisdictionType === 'STATE'
                      ? { value: 'STATE', label: 'State' }
                      : null
                }
                options={[
                  { value: 'FEDERAL', label: 'Federal' },
                  { value: 'STATE', label: 'State' },
                ]}
                onChange={(sel: any) => set({ stateJurisdictionType: sel?.value ?? '' })}
                isClearable
                placeholder='Select jurisdiction...'
              />
            </Col>
            <Col>
              <Form.Label>Zip Code</Form.Label>
              <Form.Control
                type='number'
                value={pending.zipCode}
                onChange={(e) => set({ zipCode: e.target.value })}
              />
            </Col>
          </Row>

          {/* ── Policy ── */}
          <Row className='mb-3 g-3 row-cols-1 row-cols-md-3'>
            <Col>
              <Form.Label>Policy Status</Form.Label>
              <MultiSelectWithActions
                value={allStatusFlat.filter((o) => pending.policyStatus.includes(o.value))}
                options={policyStatusGrouped}
                onChange={(sel: any) => multi('policyStatus', sel)}
              />
            </Col>
            <Col>
              <Form.Label>Policy Type</Form.Label>
              <MultiSelectWithActions
                value={policyTypeOptions.filter((o) => pending.policyType.includes(String(o.value)))}
                options={policyTypeOptions}
                onChange={(sel: any) => multi('policyType', sel)}
              />
            </Col>
            <Col>
              <Form.Label>Policy Relationship</Form.Label>
              <Form.Select
                value={policyRelationshipValue}
                onChange={(e) =>
                  set({
                    isPolicyHolder:
                      e.target.value === 'holder' ? true : e.target.value === 'family' ? false : undefined,
                    withoutPolicy:
                      e.target.value === 'no_policy'
                        ? true
                        : e.target.value === 'holder' || e.target.value === 'family'
                          ? false
                          : undefined,
                  })
                }
              >
                <option value=''>All contacts</option>
                <option value='holder'>Policy holders</option>
                <option value='family'>Family members</option>
                <option value='no_policy'>No policy</option>
              </Form.Select>
            </Col>
            <Col>
              <Form.Label>Unassigned Policies</Form.Label>
              <Select
                value={
                  pending.unassigned === true
                    ? { value: 'true', label: 'Only unassigned' }
                    : pending.unassigned === false
                      ? { value: 'false', label: 'Only assigned' }
                      : null
                }
                options={[
                  { value: 'true', label: 'Only unassigned' },
                  { value: 'false', label: 'Only assigned' },
                ]}
                onChange={(sel: any) => set({ unassigned: parseBool(sel?.value) })}
                isClearable
                placeholder='All policies'
              />
            </Col>
            <Col>
              <Form.Label>Source</Form.Label>
              <MultiSelectWithActions
                value={leadSourceOptions.filter((o) => pending.leadSource.includes(o.value))}
                options={leadSourceOptions}
                onChange={(sel: any) => multi('leadSource', sel)}
              />
            </Col>
            <Col>
              <Form.Label>Metal Level</Form.Label>
              <Select
                value={METAL_LEVELS.find((o) => o.value === pending.metalLevel) ?? null}
                options={METAL_LEVELS}
                onChange={(sel: any) => set({ metalLevel: sel?.value ?? '' })}
                isClearable
                placeholder='Select Metal Level'
              />
            </Col>
          </Row>

          {/* ── Personal attributes ── */}
          <Row className='mb-3 g-3 row-cols-1 row-cols-md-3'>
            <Col>
              <Form.Label>Immigration Status</Form.Label>
              <MultiSelectWithActions
                value={IMMIGRATION_STATUS_CHOICES.filter((o) => pending.immigrationStatus.includes(o.value))}
                options={IMMIGRATION_STATUS_CHOICES}
                onChange={(sel: any) => multi('immigrationStatus', sel)}
              />
            </Col>
            <Col>
              <Form.Label>Document Status</Form.Label>
              <MultiSelectWithActions
                value={DOCUMENT_STATUS_OPTIONS.filter((o) => pending.documentStatus.includes(o.value))}
                options={DOCUMENT_STATUS_OPTIONS}
                onChange={(sel: any) => multi('documentStatus', sel)}
              />
            </Col>
            <Col>
              <Form.Label>Payment Status</Form.Label>
              <MultiSelectWithActions
                value={PAYMENT_STATUS_OPTIONS.filter((o) => pending.paymentStatus.includes(o.value))}
                options={PAYMENT_STATUS_OPTIONS}
                onChange={(sel: any) => multi('paymentStatus', sel)}
              />
            </Col>
            <Col>
              <Form.Label>Gender (Holder)</Form.Label>
              <Select
                value={GENDER_OPTIONS.find((o) => o.value === pending.holderGender) ?? null}
                options={GENDER_OPTIONS}
                onChange={(sel: any) => set({ holderGender: sel?.value ?? '' })}
                isClearable
                placeholder='Select...'
              />
            </Col>
            <Col>
              <Form.Label>Member ID Status</Form.Label>
              <Select
                value={boolVal(pending.hasMemberId)}
                options={[
                  { value: 'true', label: 'With Member ID' },
                  { value: 'false', label: 'Without Member ID' },
                ]}
                onChange={(sel: any) => set({ hasMemberId: parseBool(sel?.value) })}
                isClearable
                placeholder='All policies'
              />
            </Col>
          </Row>

          {/* ── Commission ── */}
          <Row className='mb-3 g-3 row-cols-1 row-cols-md-3'>
            <Col>
              <Form.Label>Commission Report Status</Form.Label>
              <Select
                value={
                  pending.commissionPayable === true
                    ? { value: 'true', label: 'Considered for commission' }
                    : pending.commissionPayable === false
                      ? { value: 'false', label: 'Not considered for commission' }
                      : null
                }
                options={[
                  { value: 'true', label: 'Considered for commission' },
                  { value: 'false', label: 'Not considered for commission' },
                ]}
                onChange={(sel: any) => set({ commissionPayable: parseBool(sel?.value) })}
                isClearable
                placeholder='All policies'
              />
            </Col>
            <Col>
              <Form.Label>Commission Payment Status</Form.Label>
              <Select
                value={
                  COMMISSION_PAYMENT_STATUS_OPTIONS.find((o) => o.value === pending.commissionPaymentStatus) ?? null
                }
                options={COMMISSION_PAYMENT_STATUS_OPTIONS}
                onChange={(sel: any) => set({ commissionPaymentStatus: sel?.value ?? '' })}
                isClearable
                placeholder='All policies'
              />
            </Col>
          </Row>

          {/* ── Property / extras ── */}
          <Row className='mb-3 g-3 row-cols-1 row-cols-md-4'>
            <Col>
              <Form.Label>Owns car</Form.Label>
              <Select
                value={boolVal(pending.ownsCar)}
                options={YES_NO_OPTIONS}
                onChange={(sel: any) => set({ ownsCar: parseBool(sel?.value) })}
                isClearable
                placeholder='Any'
              />
            </Col>
            <Col>
              <Form.Label>Owns home</Form.Label>
              <Select
                value={boolVal(pending.ownsHome)}
                options={YES_NO_OPTIONS}
                onChange={(sel: any) => set({ ownsHome: parseBool(sel?.value) })}
                isClearable
                placeholder='Any'
              />
            </Col>
            <Col>
              <Form.Label>Has auto policy</Form.Label>
              <Select
                value={boolVal(pending.hasAutoPolicy)}
                options={YES_NO_OPTIONS}
                onChange={(sel: any) => set({ hasAutoPolicy: parseBool(sel?.value) })}
                isClearable
                placeholder='Any'
              />
            </Col>
            <Col>
              <Form.Label>Has home policy</Form.Label>
              <Select
                value={boolVal(pending.hasHomePolicy)}
                options={YES_NO_OPTIONS}
                onChange={(sel: any) => set({ hasHomePolicy: parseBool(sel?.value) })}
                isClearable
                placeholder='Any'
              />
            </Col>
          </Row>

          <hr />

          {/* ── Date Ranges ── */}
          <h6 className='text-muted mb-3'>Date Ranges</h6>
          <Row className='g-3 row-cols-1 row-cols-md-2 row-cols-xl-4 mb-3'>
            <Col>
              <DateInput
                id='policyCreatedFrom'
                name='policyCreatedFrom'
                label='Policy Created From'
                value={pending.policyCreatedFrom}
                onChange={handleDateChange}
              />
            </Col>
            <Col>
              <DateInput
                id='policyCreatedTo'
                name='policyCreatedTo'
                label='Policy Created To'
                value={pending.policyCreatedTo}
                onChange={handleDateChange}
              />
            </Col>
            <Col>
              <DateInput
                id='submissionDateFrom'
                name='submissionDateFrom'
                label='Submission Date From'
                value={pending.submissionDateFrom}
                onChange={handleDateChange}
              />
            </Col>
            <Col>
              <DateInput
                id='submissionDateTo'
                name='submissionDateTo'
                label='Submission Date To'
                value={pending.submissionDateTo}
                onChange={handleDateChange}
              />
            </Col>
          </Row>

          <hr />

          {/* ── Carrier Import Filters ── */}
          <h5>Carrier Import Filters</h5>
          <Row className='mb-3 g-2 align-items-end'>
            <Col xs={12} md={6} xl={3}>
              <Form.Label>Carrier Status</Form.Label>
              <Select
                value={CARRIER_STATUS_OPTIONS.filter((o) => pending.carrierStatusGeneric.includes(o.value))}
                options={CARRIER_STATUS_OPTIONS}
                onChange={(sel: any) =>
                  set({ carrierStatusGeneric: sel ? sel.map((o: any) => o.value) : [] })
                }
                isMulti
                isClearable
                placeholder='Select carrier status...'
              />
            </Col>
            <Col xs={12} md={6} xl={2}>
              <DateInput
                id='carrierLastAsOfDateAfter'
                name='carrierLastAsOfDateAfter'
                label='Carrier As-of Date From'
                value={pending.carrierLastAsOfDateAfter}
                onChange={handleDateChange}
              />
            </Col>
            <Col xs={12} md={6} xl={2}>
              <DateInput
                id='carrierLastAsOfDateBefore'
                name='carrierLastAsOfDateBefore'
                label='Carrier As-of Date To'
                value={pending.carrierLastAsOfDateBefore}
                onChange={handleDateChange}
              />
            </Col>
            <Col xs={12} md={6} xl={1}>
              <Form.Label className='small text-nowrap'>Age-In</Form.Label>
              <Select
                value={boolVal(pending.carrierIsAgeIn)}
                options={YES_NO_OPTIONS}
                onChange={(sel: any) => set({ carrierIsAgeIn: parseBool(sel?.value) })}
                isClearable
                placeholder='Any'
              />
            </Col>
            <Col xs={12} md={6} xl={1}>
              <Form.Label className='small text-nowrap'>Delinquent</Form.Label>
              <Select
                value={boolVal(pending.carrierIsDelinquent)}
                options={YES_NO_OPTIONS}
                onChange={(sel: any) => set({ carrierIsDelinquent: parseBool(sel?.value) })}
                isClearable
                placeholder='Any'
              />
            </Col>
            <Col xs={12} md={6} xl={2}>
              <Form.Label className='small text-nowrap'>Unpaid Pending</Form.Label>
              <Select
                value={boolVal(pending.carrierIsUnpaidPending)}
                options={YES_NO_OPTIONS}
                onChange={(sel: any) => set({ carrierIsUnpaidPending: parseBool(sel?.value) })}
                isClearable
                placeholder='Any'
              />
            </Col>
            <Col xs={12} md={6} xl={2}>
              <Form.Label className='small text-nowrap'>Unpaid Past Due</Form.Label>
              <Select
                value={boolVal(pending.carrierIsUnpaidPastDue)}
                options={YES_NO_OPTIONS}
                onChange={(sel: any) => set({ carrierIsUnpaidPastDue: parseBool(sel?.value) })}
                isClearable
                placeholder='Any'
              />
            </Col>
          </Row>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button variant='light' className='px-4' onClick={onHide}>Cancel</Button>
        <Button variant='primary' className='px-4' onClick={handleApply}>
          <i className='bi bi-search me-1' />Apply Filters
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
